<script lang="ts">
  // svelte/kit
  import { fade } from 'svelte/transition';
  // UI components
  import * as Dialog from '$lib/components/ui/dialog/index.js';
  import { Button } from '$lib/components/ui/button';
  import { Input } from '$lib/components/ui/input/index.js';
  import { Label } from '$lib/components/ui/label/index.js';
  // RoastLog
  import logger from '$lib/logger';
  import { roastQueue, selectedRoaster, userProfile } from '$lib/PersistedState.svelte';
  import { type Nullable } from '$lib/types/RoastLog';
  import { updateRoast } from '$lib/api';
  import { massToGrams } from '$lib/RLogUtils';
  // Other

  const errorClass = 'border border-rose-600 dark:border-red-800 dark:bg-gray-800';

  let {
    open = $bindable(true),
    qItemId
  }: {
    open: boolean;
    qItemId: string;
  } = $props();

  // make a copy of the state, so that we can enable the save/cancel
  const qItemState = $derived(roastQueue.findItemById(qItemId));
  const qItem = $derived(qItemState ? $state.snapshot(qItemState) : undefined);

  let errorMessage: Nullable<string> = $state(null);
  let saveDisabled = $state(false);
  let endMass: number | undefined = $state(undefined);
  let roastLevel: number | undefined = $state(undefined);

  $effect(() => {
    endMass = qItem?.endingMass;
    roastLevel = qItem?.roastLevel;
  });

  const handleSave = () => {
    if (qItemState === undefined || !qItemState.roastId || (!endMass && !roastLevel)) {
      return;
    }

    const roastLevelValid = validateRoastLevel(roastLevel);
    if (!roastLevelValid) {
      return;
    }

    const update: { end_mass?: number; roast_level?: number } = {};

    if (roastLevel !== undefined) {
      qItemState.roastLevel = roastLevel;
      update.roast_level = roastLevel;
    }

    if (endMass) {
      // The ending mass here is the display value. We only need to convert to grams when sending to
      // the the api, or during display for things we've fetched.
      qItemState.endingMass = endMass;
      const endMassGrams = massToGrams(selectedRoaster.massScale, endMass);

      if (endMass >= (qItemState.startingMass ?? 0)) {
        errorMessage = `End mass must be less than starting mass: ${qItem?.startingMass} ${selectedRoaster.massScale}`;
        return;
      }
      update.end_mass = endMassGrams;
    }

    errorMessage = null;

    if (update.end_mass !== undefined || update.roast_level !== undefined) {
      logger.info('Updating roast queue item', update);
      updateRoast(qItemState.roastId, update);
    }

    endMass = undefined;
    roastLevel = undefined;
    open = false;
  };

  const validateRoastLevel = (roastLevel: undefined | number) => {
    // Validate roast level
    if (roastLevel == undefined) {
      return true;
    }

    if (roastLevel < 10 || roastLevel > 130) {
      errorMessage = 'Roast level must be between 10 and 130';
      return false;
    }
    // Check for more than one decimal place
    const decimalPlaces = (roastLevel.toString().split('.')[1] || '').length;
    if (decimalPlaces > 1) {
      errorMessage = 'Roast level can have at most one decimal place';
      return false;
    }
    return true;
  };

  $effect(() => {
    saveDisabled = qItem?.endingMass === endMass && qItem?.roastLevel === roastLevel;
  });
</script>

{#if qItem}
  <Dialog.Root bind:open>
    <Dialog.Content class="w-md">
      <Dialog.Header>
        <Dialog.Title>Edit Roast Details</Dialog.Title>
        <Dialog.Description>
          Edit details for {qItem.roastable?.name} (#{qItem.roastId})
        </Dialog.Description>
      </Dialog.Header>

      <div class="grid gap-4 py-4">
        <div class="grid grid-cols-2 items-center gap-4">
          <Label for="end-mass" class="text-right">Ending mass ({selectedRoaster.massScale})</Label>
          <Input
            type="number"
            class={`${errorMessage !== null ? errorClass : ''}`}
            defaultValue={qItem.endingMass}
            min="0.1"
            step="0.1"
            bind:value={endMass}
          />
        </div>
        <div class="grid grid-cols-2 items-center gap-4">
          <Label for="roast-level" class="text-right">Roast Level</Label>
          <Input
            type="number"
            class={`${errorMessage !== null ? errorClass : ''}`}
            defaultValue={qItem.roastLevel}
            min="10"
            max="130"
            step="0.1"
            bind:value={roastLevel}
          />
          {#if errorMessage}
            <div
              class="col-span-2 text-red-600 dark:text-red-400"
              transition:fade={{ duration: 200 }}
            >
              {errorMessage}
            </div>
          {/if}
        </div>
      </div>

      <Dialog.Footer>
        <Button
          type="submit"
          onclick={() => {
            open = false;
            endMass = undefined;
            roastLevel = undefined;
          }}
          variant="destructive">Cancel</Button
        >
        <Button type="submit" onclick={handleSave} disabled={saveDisabled}>Save changes</Button>
      </Dialog.Footer>
    </Dialog.Content>
  </Dialog.Root>
{/if}
